import { useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/shared/ui/dialog";
import { cn } from "@/shared/lib/utils";
import { FirebaseImage } from "@/shared/components/FirebaseImage";

export function EvidenceThumbnails({
  urls,
  title,
  answer,
}: {
  urls: string[];
  title: string;
  answer: string;
}) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const selectedUrl = selectedIndex === null ? null : urls[selectedIndex];

  if (urls.length === 0) return null;

  const previousPhoto = () => {
    setSelectedIndex((current) => current === null ? 0 : (current - 1 + urls.length) % urls.length);
  };
  const nextPhoto = () => {
    setSelectedIndex((current) => current === null ? 0 : (current + 1) % urls.length);
  };

  return (
    <>
      <div className="flex shrink-0 items-center justify-end gap-1.5">
        {urls.slice(0, 3).map((url, index) => (
          <button
            key={url}
            type="button"
            onClick={() => setSelectedIndex(index)}
            className="group relative h-12 w-12 overflow-hidden rounded-lg border border-border/50 bg-muted transition-colors hover:border-primary/60 focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label={`Open evidence photo ${index + 1} for ${title}`}
          >
            <FirebaseImage
              src={url}
              alt={`${title} evidence ${index + 1}`}
              className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
              loading="lazy"
            />
          </button>
        ))}
        {urls.length > 3 && (
          <button
            type="button"
            onClick={() => setSelectedIndex(3)}
            className="flex h-12 w-12 items-center justify-center rounded-lg border border-border/50 bg-muted text-sm font-medium text-muted-foreground hover:border-primary/60 hover:text-foreground"
            aria-label={`Open ${urls.length - 3} more evidence photos for ${title}`}
          >
            +{urls.length - 3}
          </button>
        )}
      </div>

      <Dialog open={selectedIndex !== null} onOpenChange={(open) => !open && setSelectedIndex(null)}>
        <DialogContent className="max-w-4xl overflow-hidden border-border/40 bg-card p-0">
          <div className="flex items-start justify-between gap-4 border-b border-border/40 px-5 py-4">
            <div className="min-w-0 space-y-1">
              <DialogTitle className="text-lg font-semibold">{title}</DialogTitle>
              <DialogDescription className="text-base text-muted-foreground">
                Answer: {answer}
              </DialogDescription>
            </div>
            <DialogClose className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>
          <div className="relative flex max-h-[75vh] min-h-[420px] items-center justify-center bg-background">
            {selectedUrl && (
              <FirebaseImage
                src={selectedUrl}
                alt={`${title} evidence`}
                className="max-h-[75vh] max-w-full object-contain"
              />
            )}
            {urls.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={previousPhoto}
                  className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background/80 text-foreground shadow hover:bg-background"
                  aria-label="Previous evidence photo"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={nextPhoto}
                  className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background/80 text-foreground shadow hover:bg-background"
                  aria-label="Next evidence photo"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                <div className="absolute bottom-3 rounded-full bg-background/80 px-3 py-1 text-sm text-muted-foreground">
                  {(selectedIndex ?? 0) + 1} / {urls.length}
                </div>
              </>
            )}
          </div>
          {urls.length > 1 && (
            <div className="overflow-x-auto border-t border-border/40 bg-card px-5 py-3">
              <div className="flex w-max min-w-full justify-center gap-2">
                {urls.map((url, index) => {
                  const isSelected = index === selectedIndex;
                  return (
                    <button
                      key={`${url}-${index}`}
                      type="button"
                      onClick={() => setSelectedIndex(index)}
                      className={cn(
                        "h-16 w-16 shrink-0 overflow-hidden rounded-lg border bg-muted transition",
                        isSelected
                          ? "border-primary ring-2 ring-primary/40"
                          : "border-border/50 opacity-70 hover:border-primary/60 hover:opacity-100"
                      )}
                      aria-label={`Show evidence photo ${index + 1}`}
                    >
                      <FirebaseImage
                        src={url}
                        alt={`${title} evidence preview ${index + 1}`}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
